"use client";

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import * as React from "react";

import { cn } from "../../lib/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<React.ComponentRef<typeof AccordionPrimitive.Item>, React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>>(
  ({ className, ...props }, ref) => <AccordionPrimitive.Item ref={ref} className={cn("border-b", className)} {...props} />
);
AccordionItem.displayName = "AccordionItem";

interface AccordionTriggerProps extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  hideChevron?: boolean;
  chevronRef?: React.RefObject<HTMLDivElement>;
  className?: string;
  children: React.ReactNode;
  id?: string;
  key?: string;
  hidden?: boolean;
  rotateChevron?: boolean;
  /** Render trigger as a div instead of button to allow nesting interactive elements */
  asDiv?: boolean;
}

const AccordionTrigger = React.forwardRef<React.ComponentRef<typeof AccordionPrimitive.Trigger>, AccordionTriggerProps>(
  ({ className, hideChevron, children, chevronRef, rotateChevron = false, asDiv = false, ...props }, ref) => {
    const triggerClassName = cn("group flex flex-1 items-center justify-between gap-3 py-4 font-medium transition-all hover:underline", className);

    const chevron = !hideChevron ? (
      <div ref={chevronRef}>
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 transition-transform duration-200", rotateChevron ? "rotate-180" : "group-data-[state=open]:rotate-180")}
        />
      </div>
    ) : null;

    return (
      <AccordionPrimitive.Header className="flex w-full justify-between">
        {asDiv ? (
          <AccordionPrimitive.Trigger asChild ref={ref} className={triggerClassName} {...props}>
            <div>
              {children}
              {chevron}
            </div>
          </AccordionPrimitive.Trigger>
        ) : (
          <AccordionPrimitive.Trigger ref={ref} className={triggerClassName} {...props}>
            {children}
            {chevron}
          </AccordionPrimitive.Trigger>
        )}
      </AccordionPrimitive.Header>
    );
  }
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
    animationDuration?: string;
    forceMount?: true;
    /** Skip animation entirely for instant open/close */
    skipAnimation?: boolean;
  }
>(({ className, children, animationDuration = "0.15s", forceMount, skipAnimation, ...props }, ref) => {
  // Simplified version: No MutationObserver, no animation state tracking
  // This significantly improves performance for accordions
  const contentStateClasses = skipAnimation
    ? "data-[state=closed]:hidden data-[state=open]:block"
    : "data-[state=closed]:h-0 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down";

  return (
    <AccordionPrimitive.Content
      ref={ref}
      forceMount={forceMount}
      className={cn("overflow-hidden text-sm", contentStateClasses, className)}
      style={
        skipAnimation
          ? undefined
          : ({
              "--accordion-duration": animationDuration,
            } as React.CSSProperties)
      }
      {...props}
    >
      {children}
    </AccordionPrimitive.Content>
  );
});
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
